// MailAiGuideCard — 내 정보 «메일 AI 지침». 메일(Roundcube) 화면의 「AI 요약」「AI 답장 초안」이
// 기본 지시문 뒤에 이 지침을 덧붙인다(서버 routers/mail_ai.py · 플러그인 gw_ai 가 /guide/resolve 로 받아 감).
//
// 🔴 권한 판정은 받은 user(= MyPage 가 /api/auth/me 로 받은 profile)로만 한다 — getMe·localStorage 를 직접 읽지 않는다.
// 🔴 메일 팝업의 「지침 설정」이 /my#mail-ai 로 보낸다 → 지침을 받은 뒤 이 카드로 스크롤한다.
import { useEffect, useRef, useState } from 'react';
import { mailAiAPI } from '../api/client';
import { canSeeMenu } from '../utils/menuAccess';
import { card as cardBase, input as inputBase, RADIUS } from '../utils/uiStyles';
import { Button } from './ui';

const DEFAULT_MAX = 1000;
const FIELDS = [
  { key: 'summary', label: '요약 지침', placeholder: '예) 3줄 이내로. 마감일·요청사항은 맨 위에.' },
  { key: 'reply', label: '답장 초안 지침', placeholder: '예) 존댓말로 간결하게. 끝에 "아워박스 DX팀 ○○○ 드림" 서명.' },
];

export default function MailAiGuideCard({ user, mobile }) {
  const allowed = canSeeMenu(user, 'mail2');
  const rootRef = useRef(null);
  const [guide, setGuide] = useState({ summary: '', reply: '' });
  const [defaults, setDefaults] = useState({ summary: '', reply: '' });
  const [maxLen, setMaxLen] = useState(DEFAULT_MAX);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState({ summary: false, reply: false });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);   // { ok, text }

  useEffect(() => {
    if (!allowed) return undefined;
    let dead = false;
    (async () => {
      try {
        const r = await mailAiAPI.getGuide();
        const d = r.data || {};
        if (dead) return;
        setGuide({ summary: d.summary_guide || '', reply: d.reply_guide || '' });
        setDefaults({ summary: (d.defaults && d.defaults.summary) || '', reply: (d.defaults && d.defaults.reply) || '' });
        if (d.max_len) setMaxLen(d.max_len);
        setLoaded(true);
        if (window.location.hash === '#mail-ai') {
          // 내용이 그려진 다음 틀에서 스크롤해야 위치가 맞는다.
          setTimeout(() => {
            const el = rootRef.current;
            if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
          }, 0);
        }
      } catch (e) {
        if (!dead) setMsg({ ok: false, text: '불러오지 못했습니다' });
      }
    })();
    return () => { dead = true; };
  }, [allowed]);

  if (!allowed) return null;

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await mailAiAPI.updateGuide({ summary_guide: guide.summary, reply_guide: guide.reply });
      const d = (r && r.data) || {};
      // 서버가 정리한 값(앞뒤 공백·제어문자 제거)으로 맞춘다.
      setGuide((g) => ({
        summary: typeof d.summary_guide === 'string' ? d.summary_guide : g.summary,
        reply: typeof d.reply_guide === 'string' ? d.reply_guide : g.reply,
      }));
      setMsg({ ok: true, text: '저장했습니다' });
    } catch (e) {
      const status = e && e.response ? e.response.status : null;
      setMsg({ ok: false, text: status === 422 ? '1000자 이하로 써 주세요' : '저장하지 못했습니다' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="mail-ai" ref={rootRef}
      style={{ ...cardBase, marginBottom: mobile ? '14px' : '16px', boxSizing: 'border-box' }}>
      <h2 style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)', margin: '0 0 14px' }}>메일 AI 지침</h2>
      <p style={{ fontSize: '13px', color: 'var(--gw-secondary, #64635f)', marginBottom: '12px', lineHeight: 1.6 }}>
        메일 화면의 「AI 요약」「AI 답장 초안」에 내 지침이 덧붙습니다. 비워 두면 기본 지침만 씁니다.
      </p>
      {FIELDS.map((f) => {
        const id = `mail-ai-${f.key}`;
        const isOpen = open[f.key];
        return (
          <div key={f.key} style={{ marginBottom: '14px' }}>
            <label htmlFor={id} style={{ display: 'block', fontSize: '12px', color: 'var(--gw-secondary, #64635f)', fontWeight: '500', marginBottom: '4px' }}>{f.label}</label>
            <div style={{ position: 'relative' }}>
              <textarea id={id} rows={4} maxLength={maxLen} value={guide[f.key]} placeholder={f.placeholder} disabled={!loaded}
                onChange={(e) => { const v = e.target.value; setGuide((g) => ({ ...g, [f.key]: v })); }}
                style={{ ...inputBase, padding: '8px 10px 22px', lineHeight: 1.5, fontFamily: 'inherit', resize: 'vertical', background: loaded ? 'var(--gw-surface, #fff)' : 'var(--gw-subtle, #fbfbfa)' }} />
              <span style={{ position: 'absolute', right: '10px', bottom: '8px', fontSize: '11px', color: 'var(--gw-muted, #92918d)', pointerEvents: 'none' }}>
                {guide[f.key].length}/{maxLen}
              </span>
            </div>
            <button type="button" onClick={() => setOpen((o) => ({ ...o, [f.key]: !o[f.key] }))}
              aria-expanded={isOpen} aria-controls={`${id}-default`} aria-label={`${f.label} 기본 지침 ${isOpen ? '접기' : '보기'}`}
              style={{ marginTop: '6px', padding: '2px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--gw-secondary, #64635f)', textDecoration: 'underline' }}>
              {isOpen ? '기본 지침 접기' : '기본 지침 보기'}
            </button>
            {isOpen && (
              <div id={`${id}-default`}
                style={{ marginTop: '6px', padding: '8px 10px', background: 'var(--gw-subtle, #fbfbfa)', border: '1px solid var(--gw-line, #eeeeec)', borderRadius: RADIUS.md, fontSize: '12px', color: 'var(--gw-secondary, #64635f)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {defaults[f.key] || '기본 지침을 불러오지 못했습니다.'}
              </div>
            )}
          </div>
        );
      })}
      {msg && <div role="status" style={{ fontSize: '13px', color: msg.ok ? 'var(--gw-green-ink, #426b55)' : 'var(--gw-red-ink, #a4564d)', marginBottom: '10px' }}>{msg.text}</div>}
      <Button variant="primary" onClick={save} disabled={busy || !loaded} aria-label="메일 AI 지침 저장" style={{ width: mobile ? '100%' : undefined }}>
        {busy ? '저장 중…' : '저장'}
      </Button>
    </div>
  );
}
